import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { MultiSelectLiveDropdownPropsSelectItems } from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown.d";
import Popup from "../../../../components/Popup/Popup";
import { useSession } from "../../../../contexts/SessionContext";
import translate from "../../../../services/Translate";
import { ExternalUserInvitation, SessionInvitationModalProps } from "./SessionInvitationModal.d";
import { inviteToEphemeralSession } from "./SessionInvitationModal.function";

const SessionInvitationModal = ({ onClose, sessionId, show }: SessionInvitationModalProps) => {
  const { language, showAlert } = useSession();
  const [externalUsers, setExternalUsers] = useState<ExternalUserInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState<MultiSelectLiveDropdownPropsSelectItems[]>([]);
  const [sendEmail, setSendEmail] = useState(true);

  useEffect(() => {
    if (show) {
      setExternalUsers([]);
      setRecipients([]);
      setSendEmail(true);
    }
  }, [show]);

  const _updateExternalUser = (index: number, field: keyof ExternalUserInvitation, value: string) =>
    setExternalUsers((users) =>
      users.map((externalUser, userIndex) => (userIndex === index ? { ...externalUser, [field]: value } : externalUser))
    );

  const _submit = () => {
    inviteToEphemeralSession(
      sessionId,
      {
        cohort_ids: recipients.filter(({ category }) => category === "cohorts").map(({ value }) => value.id),
        external_users: externalUsers.map(({ email, full_name }) => ({
          email: email.trim().toLowerCase(),
          full_name: full_name.trim(),
        })),
        send_email: sendEmail,
        user_ids: recipients.filter(({ category }) => category === "users").map(({ value }) => value.id),
      },
      language,
      setLoading,
      showAlert,
      onClose
    );
  };

  const externalUsersAreValid = externalUsers.every(
    ({ email, full_name }) => full_name.trim().length > 0 && /^\S+@\S+\.\S+$/.test(email.trim())
  );
  const canSubmit = (recipients.length > 0 || externalUsers.length > 0) && externalUsersAreValid;

  return (
    <Popup
      show={show}
      onClose={() => onClose()}
      modalTitle={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.TITLE")}
      size="lg"
      footerContent={
        <React.Fragment>
          <DstButton
            variant="light"
            value={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.BUTTONS.CANCEL")}
            clickFunction={onClose}
          />
          <DstButton
            disabled={!canSubmit || loading}
            loading={loading}
            value={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.BUTTONS.SUBMIT")}
            clickFunction={_submit}
          />
        </React.Fragment>
      }
    >
      <React.Fragment>
        <Form.Group className="mb-4">
          <Form.Label>{translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.INTERNAL_RECIPIENTS")}</Form.Label>
          <MultiSelectLiveDropdown
            key={`${sessionId}-${show}`}
            category={["users", "cohorts"]}
            initialSelectedItems={recipients}
            label={
              recipients.length > 0
                ? translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.RECIPIENTS_SELECTED").replace(
                    "{{COUNT}}",
                    String(recipients.length)
                  )
                : translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.SELECT_RECIPIENTS")
            }
            language={language}
            onConfirm={setRecipients}
            showFilterButton
            toggleClass="w-100 border"
          />
          <Form.Text muted>{translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.INTERNAL_HELP")}</Form.Text>
        </Form.Group>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <Form.Label className="mb-0">
            {translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.EXTERNAL_RECIPIENTS")}
          </Form.Label>
          <DstButton
            btnImageBefore={<span className="dst-icon-plus me-2" />}
            value={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.BUTTONS.ADD_EXTERNAL")}
            variant="light"
            clickFunction={() => setExternalUsers((users) => [...users, { email: "", full_name: "" }])}
          />
        </div>

        {externalUsers.map((externalUser, index) => (
          <div className="d-flex align-items-start gap-2 mb-2" key={index}>
            <Form.Control
              aria-label={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.FULL_NAME")}
              placeholder={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.FULL_NAME")}
              value={externalUser.full_name}
              onChange={(event) => _updateExternalUser(index, "full_name", event.target.value)}
            />
            <Form.Control
              aria-label={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.EMAIL")}
              placeholder={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.EMAIL")}
              type="email"
              value={externalUser.email}
              onChange={(event) => _updateExternalUser(index, "email", event.target.value)}
            />
            <DstButton
              btnImageBefore={<span className="dst-icon-trash-01" />}
              tooltip={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.BUTTONS.REMOVE_EXTERNAL")}
              value=""
              variant="light"
              clickFunction={() => setExternalUsers((users) => users.filter((_, userIndex) => userIndex !== index))}
            />
          </div>
        ))}

        <Form.Check
          className="mt-4"
          checked={sendEmail}
          label={translate(language, "PAGES.EPHEMERAL_SESSION.INVITATION.SEND_EMAIL")}
          onChange={(event) => setSendEmail(event.target.checked)}
          type="switch"
        />
      </React.Fragment>
    </Popup>
  );
};

export default SessionInvitationModal;
